"use client";
import { useState } from "react";
import Link from "next/link";
import { PanelLeftClose, PanelLeft, LogOut, X } from "lucide-react";
import { SidebarNav } from "./sidebar-nav";
import { cn } from "@/lib/utils";

// 어드민 셸 — 데스크톱은 인라인 collapsible 사이드바, 모바일은 오버레이 드로어(백드롭).
// 접힘 상태는 쿠키 영속(서버가 initialCollapsed 주입 → SSR/hydration 일치).
export function AdminShell({
  userName,
  initialCollapsed,
  version,
  permissions = [],
  children,
}: {
  userName: string | null;
  initialCollapsed: boolean;
  version: { sha: string; branch: string };
  permissions?: string[];
  children: React.ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  function setState(next: boolean) {
    setCollapsed(next);
    document.cookie = `sidebar_collapsed=${next ? "1" : "0"}; path=/; max-age=31536000`;
  }
  const toggle = () => setState(!collapsed);
  // 모바일에서 메뉴 항목 탭 시 드로어 자동 닫기(데스크톱은 유지).
  function onNavigate() {
    if (typeof window !== "undefined" && window.innerWidth < 768) setState(true);
  }

  return (
    <div className="flex min-h-screen bg-neutral-50 text-neutral-900">
      {/* 모바일 백드롭 — 드로어 열렸을 때만. 탭하면 닫힘. */}
      {!collapsed && (
        <div
          onClick={toggle}
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          aria-hidden
        />
      )}

      {!collapsed && (
        <aside
          className={cn(
            "z-40 flex w-64 shrink-0 flex-col border-r border-neutral-200 bg-white",
            "fixed inset-y-0 left-0 md:static md:z-auto md:w-56", // 모바일: 오버레이 / 데스크톱: 인라인
          )}
        >
          <div className="flex items-center border-b border-neutral-200 px-4 py-4">
            <div>
              <Link href="/" onClick={onNavigate} className="text-base font-semibold tracking-tight">
                success
              </Link>
              <p className="mt-0.5 text-[11px] text-neutral-400">아이디어 → 매출 라이프사이클</p>
            </div>
            {/* 모바일 전용 닫기 버튼 */}
            <button
              onClick={toggle}
              className="ml-auto rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 md:hidden"
              aria-label="메뉴 닫기"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            <SidebarNav onNavigate={onNavigate} permissions={permissions} />
          </div>
          <div className="border-t border-neutral-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-900 text-[11px] font-semibold text-white">
                {(userName ?? "?").slice(0, 1)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium">{userName ?? "비로그인"}</p>
                <p className="text-[10px] text-neutral-400">슈퍼어드민</p>
              </div>
              <a href="/admin/auth/logout" className="text-neutral-400 hover:text-neutral-900" title="로그아웃">
                <LogOut className="h-3.5 w-3.5" />
              </a>
            </div>
            <VersionTag version={version} className="mt-2" />
          </div>
        </aside>
      )}

      <main className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-neutral-200 bg-white px-3 py-1.5">
          <button
            onClick={toggle}
            className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
            title={collapsed ? "메뉴 펼치기" : "메뉴 숨기기"}
            aria-label="사이드바 토글"
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
          {collapsed && (
            <Link href="/" className="text-sm font-semibold tracking-tight">
              success
            </Link>
          )}
          <VersionTag version={version} className="ml-auto" />
        </div>
        <div className="flex-1 px-4 py-5 sm:px-5">{children}</div>
      </main>
    </div>
  );
}

// 버전 태그(브랜치 · 숏해시) — ziczzic 방식.
function VersionTag({ version, className }: { version: { sha: string; branch: string }; className?: string }) {
  return (
    <span className={"inline-flex items-center gap-1 font-mono text-[10px] text-neutral-400 " + (className ?? "")}>
      <span className="rounded bg-neutral-100 px-1 py-0.5 text-neutral-500">{version.branch}</span>
      <span title="git short hash">{version.sha}</span>
    </span>
  );
}
